/* eslint-disable max-lines -- structural: this file owns both API and
   static-option dropdown renderers (read-only, editable, and form) plus
   the bulk-edit clear UX. Splitting it adds indirection without value. */
/**
 * Dropdown Field Type
 *
 * Provides configuration and renderers for dropdown fields:
 * - Read-only column renderer
 * - Editable column renderer (supports both API-backed and static options)
 * - Form field renderer (API-backed and static options)
 */

import { memo, useCallback } from 'react';
import type { ColumnDef, CellContext } from '@tanstack/react-table';
import type { ApiResponse } from 'apisauce';
import type { Response } from 'types';
import type { Option } from 'components/inputs/Select';
import type { ComboBoxOption } from 'components/inputs/types';
import { Select, ComboBox, Autocomplete, Chip } from 'components';
import { Tooltip } from 'components/tooltip';
import { CaretDown } from '@phosphor-icons/react';
import { Avatar } from 'components/avatar';
import { t } from '@lingui/macro';
import { toast } from 'react-toastify';
import {
  EditableDropdownCell,
  ReadOnlyDropdownCell,
  TableTooltip,
  SharedEditableCell,
  useEditableContextOptional,
} from '../../components';
import { formInputSx, CellContainer, InvalidTag, Tag, TagsContainer } from '../../styles';
import type {
  FieldTypeConfig,
  FormFieldProps,
  ColumnRenderer,
  RenderOptions,
  CustomColumn,
  CreateableColumn,
} from '../../types';
import type { CustomFieldValue } from '../../../customFields';
import { clearEditOnSuccess } from '../inlineEditUtils';
import { validateFieldValue } from '../../validation';
import { FieldError, shouldShowFieldError } from './fieldError';
import { M } from './columnWidths';
// Import shared cell value utility
import { getCellValue } from './cellValue';
import { createFilterOptions, type FilterOptionsState } from '@mui/material';
import { BULK_EDIT_KEEP_CURRENT, BULK_EDIT_CLEAR } from '../../constants';
import { ClearedFieldIndicator } from '../../components/ClearedFieldIndicator';
import { ClearFieldOption } from '../../components/ClearFieldOption';
import { ClearFieldFocusContainer } from '../../components/ClearFieldButton';
import { getBulkEditClearState } from '../../hooks/useBulkEditClear';
import {
  brandOptionAllowedForSupplierContact,
  supplierContactOptionHasBrandAccess,
} from '../brandSupplierBulkEditAccess';

type BulkBrandSupplierErrors = {
  brand?: string;
  supplierContactPerson?: string;
  severity?: { brand?: 'error' | 'warning'; supplierContactPerson?: 'error' | 'warning' };
};

const defaultComboBoxFilter = createFilterOptions<ComboBoxOption>();

// ============================================================================
// Validation Helper
// ============================================================================

/**
 * Check if a value is valid (exists in the allowed options)
 * Returns true if value is empty or found in options, false if value exists but not in options.
 * Uses case-insensitive label comparison so API values like "published" match option "Published".
 */
const isValueValid = (
  cellValue: string,
  options?: Array<{ id: string | number; label: string }>
): boolean => {
  // Empty values are always valid
  if (!cellValue) return true;
  // If no options provided, we can't validate - assume valid
  if (!options || options.length === 0) return true;
  // Check if value exists in options (match by label or id; label comparison is case-insensitive)
  const cellLower = cellValue.toLowerCase();
  return options.some(
    opt =>
      (typeof opt.label === 'string' && opt.label.toLowerCase() === cellLower) ||
      String(opt.id) === cellValue
  );
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Helper to get display value from cell context for dropdown
 * Uses getCellValue (respects accessorFn) and handles array/object formats
 */
const getCellDisplayValue = <T extends { id: number | string }>(
  context: CellContext<T, unknown>
): string => {
  const value = getCellValue(context, context.column.id);

  if (value === undefined || value === null) {
    return '';
  }

  // Handle array values (multi-select)
  if (Array.isArray(value)) {
    return value.join(', ');
  }

  // Handle object with 'name' property
  if (typeof value === 'object' && 'name' in value) {
    return String((value as { name: string }).name);
  }

  return String(value);
};

// ============================================================================
// Form Field Renderer
// ============================================================================

// Import getLookupApi lazily to avoid circular dependencies
let getLookupApiFn: ((columnId: string) => unknown) | null = null;

/**
 * Set the getLookupApi function (called from index.ts to avoid circular deps)
 */
export const setGetLookupApi = (fn: (columnId: string) => unknown): void => {
  getLookupApiFn = fn;
};

/**
 * Render a dropdown field (API-backed or static options) for forms
 * Uses ComboBox for both API and static options for consistent styling
 */
export const renderDropdownField = ({
  col,
  value,
  valueForValidation,
  onChange,
  disabled,
  formContext,
  bulkEditContext,
  validationContext,
}: FormFieldProps): React.ReactNode => {
  const emptyDisplayNode = (col as { emptyDisplayNode?: React.ReactNode }).emptyDisplayNode;
  const valueEmpty = value === null || value === undefined || value === '';
  const showEmptyChip = valueEmpty && emptyDisplayNode;

  // Hierarchy child validation (runs before Zod to provide specific error messages)
  const parentCfId = (col as { parentCustomFieldId?: number }).parentCustomFieldId;
  let hierarchyError: string | undefined;

  if (parentCfId && formContext) {
    const { formValues, selectedItems } = formContext as {
      formValues?: Record<string, unknown>;
      selectedItems?: Array<{
        customFields?: Array<{
          customFieldId: number;
          itemCustomFieldAllowedValueId?: number | null;
        }>;
      }>;
    };

    const pKey = Object.keys(formValues || {}).find(k => k.startsWith(`cf_${parentCfId}_`));
    const pFormVal = pKey ? formValues?.[pKey] : undefined;
    // Parent counts as "changed" both when the user picked a new value AND when
    // they explicitly cleared it — the child can't keep its old value either way.
    const parentWasChanged =
      (pFormVal !== null &&
        pFormVal !== undefined &&
        typeof pFormVal === 'object' &&
        'id' in pFormVal) ||
      pFormVal === BULK_EDIT_CLEAR;

    // Case 1: Child has a value but it doesn't match the current parent
    if (value && typeof value === 'object' && value !== null) {
      let effectiveParentId: number | null = null;
      if (pFormVal && typeof pFormVal === 'object' && pFormVal !== null) {
        effectiveParentId = Number((pFormVal as { id?: string | number }).id) || null;
      } else if (selectedItems && selectedItems.length > 0) {
        const getP = (item: {
          customFields?: Array<{
            customFieldId: number;
            itemCustomFieldAllowedValueId?: number | null;
          }>;
        }) =>
          item.customFields?.find(cf => cf.customFieldId === parentCfId)
            ?.itemCustomFieldAllowedValueId;
        const first = getP(selectedItems[0]);
        if (first && selectedItems.every(item => getP(item) === first)) {
          effectiveParentId = first;
        }
      }
      if (effectiveParentId) {
        const childId = Number((value as { id?: string | number }).id);
        const childOption = (col.options || []).find(
          (opt: ComboBoxOption) => Number(opt.id) === childId
        );
        if (childOption && childOption.customProps?.parentId !== effectiveParentId) {
          const parentName = (col as { parentCustomFieldName?: string }).parentCustomFieldName;
          hierarchyError = t`Select a value that corresponds to ${parentName || 'parent'}`;
        }
      }
    }

    // Case 2: parent changed and child is empty (but not via an explicit
    // "Set empty" — BULK_EDIT_CLEAR is resolved intent, do NOT error).
    const childCleared = valueForValidation === BULK_EDIT_CLEAR;
    if (!hierarchyError && parentWasChanged && !childCleared && (!value || value === '')) {
      const childCfId = (col as { customFieldId?: number }).customFieldId;
      const itemsHadChildValues =
        childCfId != null &&
        (selectedItems || []).some(
          item =>
            item.customFields?.some(
              cf => cf.customFieldId === childCfId && cf.itemCustomFieldAllowedValueId
            )
        );
      if (itemsHadChildValues) {
        const parentName = (col as { parentCustomFieldName?: string }).parentCustomFieldName;
        hierarchyError = t`Select a value that corresponds to ${parentName || 'parent'}`;
      }
    }
  }

  // Brand/contact conflict errors default to 'warning' (either side can be
  // changed); dl-59982 cascade errors are flagged 'error' by the hook.
  const bulkErrs = (formContext as { bulkEditBrandSupplierErrors?: BulkBrandSupplierErrors })
    ?.bulkEditBrandSupplierErrors;
  const bulkKey = col.key === 'brand' || col.key === 'supplierContactPerson' ? col.key : undefined;
  const bulkConflictError = bulkKey ? bulkErrs?.[bulkKey] : undefined;
  const valueToValidate = valueForValidation !== undefined ? valueForValidation : value;
  let errorMessage = hierarchyError ?? bulkConflictError;
  if (!errorMessage) {
    const validation = validateFieldValue({ ...col, type: 'dd' }, valueToValidate);
    errorMessage = !validation.success ? validation.error : undefined;
  }
  const showError = Boolean(bulkConflictError) || shouldShowFieldError(col.key, validationContext);
  const errorSeverity: 'error' | 'warning' =
    hierarchyError || !bulkConflictError
      ? 'error'
      : (bulkKey && bulkErrs?.severity?.[bulkKey]) || 'warning';

  const handleChange = (newValue: unknown) => {
    onChange(col.key, newValue);
    validationContext?.onFieldTouched?.(col.key);
  };

  const { isBulkEdit, isCleared, showClearOption, handleClear, handleUndoClear } =
    getBulkEditClearState({
      value,
      valueForValidation,
      valueEmpty,
      colKey: col.key,
      required: (col as { required?: boolean }).required,
      onChange,
      bulkEditContext,
    });

  // BULK_EDIT_CLEAR on a dropdown is always an explicit intent:
  // - The user clicked "Set empty" inside the dropdown menu, or
  // - The parent was explicitly cleared and we cascaded the clear down (the
  //   only programmatic path that still writes BULK_EDIT_CLEAR after dl-59982,
  //   when there is no valid option left to pick).
  // Render the red ClearedFieldIndicator so the intent is visible.
  //
  // EXCEPTION: when the parent itself is BULK_EDIT_CLEAR, do NOT let the user
  // undo the child's clear — there is no parent to pick a valid child for, and
  // undoing here would leave the child as KEEP_CURRENT next to a cleared
  // parent (the dropdown becomes disabled and the user gets stuck). They must
  // undo the parent first. [dl-59982]
  const isSupplierContact = col.key === 'supplierContactPerson';
  const isHierarchyChild = parentCfId != null;
  const fv = (formContext as { formValues?: Record<string, unknown> } | undefined)?.formValues;
  const parentKey = isHierarchyChild
    ? Object.keys(fv || {}).find(k => k.startsWith(`cf_${parentCfId}_`))
    : undefined;
  const parentBeingCleared =
    (isHierarchyChild && parentKey != null && fv?.[parentKey] === BULK_EDIT_CLEAR) ||
    (isSupplierContact &&
      (fv?.supplier === BULK_EDIT_CLEAR || fv?.styleSupplier === BULK_EDIT_CLEAR));
  if (isBulkEdit && isCleared && !bulkEditContext?.hideSetEmpty) {
    const indicator = (
      <ClearedFieldIndicator
        fieldName={col.label}
        onClick={parentBeingCleared ? undefined : handleUndoClear}
      />
    );
    // Hierarchy children explain on hover why the cleared child can't be
    // undone while the parent is cleared. Supplier contact already gets the
    // equivalent tooltip from BulkEditModal (disabledTooltip).
    const pName = (col as { parentCustomFieldName?: string }).parentCustomFieldName;
    return (
      <div key={col.key}>
        {parentBeingCleared && isHierarchyChild ? (
          <Tooltip
            title={
              pName ? t`Set a value for ${pName} first` : t`Set a value for the parent field first`
            }
            placement="top"
          >
            <div>{indicator}</div>
          </Tooltip>
        ) : (
          indicator
        )}
      </div>
    );
  }

  // Build avatar adornment for selected value (used by both API and static dropdowns)
  const selectedLabel =
    col.showAvatar && value
      ? typeof value === 'string'
        ? value
        : typeof value === 'object' && value !== null && 'label' in value
          ? (value as { label?: string }).label
          : typeof value === 'object' && value !== null && 'name' in value
            ? (value as { name?: string }).name
            : undefined
      : undefined;
  const avatarAdornment = selectedLabel ? <Avatar size="xs" letters={selectedLabel} /> : undefined;

  // Get API config: explicit > registry > static options
  const apiConfig =
    col.api || (getLookupApiFn ? (getLookupApiFn(col.key) as typeof col.api) : null);

  if (apiConfig) {
    const clearOptionApi: ComboBoxOption = {
      id: '__clear__',
      label: t`Set empty`,
      CustomElement: ClearFieldOption,
    };
    const apiWithClear = {
      ...apiConfig,
      responseTransform: (data: unknown) => {
        const opts = apiConfig.responseTransform(data);
        return showClearOption ? [...opts, clearOptionApi] : opts;
      },
    };

    const needsBulkBrandSupplierFilter =
      bulkEditContext?.isBulkEdit &&
      !!formContext &&
      (col.key === 'brand' || col.key === 'supplierContactPerson');

    const bulkEditBrandSupplierFilterOptions = needsBulkBrandSupplierFilter
      ? (opts: ComboBoxOption[], state?: FilterOptionsState<ComboBoxOption>) => {
          const ctx = formContext as {
            formValues?: Record<string, unknown>;
            effectiveBrandId?: number;
            /** Resolved contact with API customProps when form has keep-current */
            effectiveSupplierContact?: unknown;
          };
          let narrowed = opts;
          if (col.key === 'supplierContactPerson') {
            const brandId = ctx.effectiveBrandId;
            if (brandId != null && !Number.isNaN(Number(brandId))) {
              const bid = Number(brandId);
              narrowed = narrowed.filter(o => supplierContactOptionHasBrandAccess(o, bid));
            }
          } else if (col.key === 'brand') {
            const contact = ctx.effectiveSupplierContact ?? ctx.formValues?.supplierContactPerson;
            narrowed = narrowed.filter(o => brandOptionAllowedForSupplierContact(o, contact));
          }
          return state ? defaultComboBoxFilter(narrowed, state) : narrowed;
        }
      : undefined;

    return (
      <ClearFieldFocusContainer key={col.key}>
        <ComboBox
          id={col.key}
          label={col.label}
          placeholder={showEmptyChip ? '' : col.placeholder}
          api={apiWithClear}
          apiVarsContext={formContext}
          allowEmptySearchFetch={Boolean(bulkEditContext?.isBulkEdit && apiConfig.search)}
          disableOnEmptyApiOptions={!bulkEditContext?.isBulkEdit}
          inputVariant="outlined"
          labelSeparate
          freeSolo={false}
          forcePopupIcon
          popupIcon={<CaretDown size={20} />}
          hasAvatar={col.showAvatar}
          {...(bulkEditBrandSupplierFilterOptions
            ? { filterOptions: bulkEditBrandSupplierFilterOptions }
            : {})}
          value={(value as string) || null}
          onChange={(_, newValue, reason) => {
            if (reason === 'clear' && bulkEditContext?.isBulkEdit) {
              bulkEditContext.onKeepCurrent();
              return;
            }
            if (reason === 'clear') {
              handleChange(null);
              return;
            }
            if ((newValue as ComboBoxOption)?.id === '__clear__') {
              handleChange(bulkEditContext?.isBulkEdit ? BULK_EDIT_CLEAR : null);
            } else {
              handleChange(newValue);
            }
          }}
          disabled={disabled}
          emptyOptionsPlaceholder={
            (col as { emptyOptionsPlaceholder?: string }).emptyOptionsPlaceholder
          }
          InputProps={
            showEmptyChip
              ? { startAdornment: emptyDisplayNode }
              : avatarAdornment
                ? { startAdornment: avatarAdornment }
                : undefined
          }
        />
        <FieldError error={errorMessage} show={showError} severity={errorSeverity} />
      </ClearFieldFocusContainer>
    );
  }

  // Filter options for hierarchy child fields in bulk edit form context
  let effectiveOptions = col.options || [];
  const parentCustomFieldId = (col as { parentCustomFieldId?: number }).parentCustomFieldId;

  if (parentCustomFieldId && formContext) {
    const { formValues, selectedItems } = formContext as {
      formValues?: Record<string, unknown>;
      selectedItems?: Array<{
        customFields?: Array<{
          customFieldId: number;
          itemCustomFieldAllowedValueId?: number | null;
        }>;
      }>;
    };

    // Check if parent has been selected in the form
    const parentFieldKey = Object.keys(formValues || {}).find(key =>
      key.startsWith(`cf_${parentCustomFieldId}_`)
    );
    const parentFormValue = parentFieldKey ? formValues?.[parentFieldKey] : undefined;

    if (parentFormValue && typeof parentFormValue === 'object' && parentFormValue !== null) {
      // Parent selected in form — filter by that parent's id
      const parentId = Number((parentFormValue as { id?: string | number }).id);
      if (parentId) {
        effectiveOptions = effectiveOptions.filter(
          (opt: ComboBoxOption) => opt.customProps?.parentId === parentId
        );
      }
    } else if (selectedItems && selectedItems.length > 0) {
      // No parent selected in form — check if all items share the same parent value
      const getParentValue = (item: {
        customFields?: Array<{
          customFieldId: number;
          itemCustomFieldAllowedValueId?: number | null;
        }>;
      }) =>
        item.customFields?.find(cf => cf.customFieldId === parentCustomFieldId)
          ?.itemCustomFieldAllowedValueId;

      const firstParentValue = getParentValue(selectedItems[0]);
      const allSame =
        firstParentValue && selectedItems.every(item => getParentValue(item) === firstParentValue);

      if (allSame) {
        effectiveOptions = effectiveOptions.filter(
          (opt: ComboBoxOption) => opt.customProps?.parentId === firstParentValue
        );
      }
    }
  }

  // Use ComboBox with static options for consistent styling with API-backed dropdowns
  // Convert options to ComboBox format if needed
  const comboBoxOptions: ComboBoxOption[] = effectiveOptions
    ? effectiveOptions.map(opt => ({
        id: typeof opt === 'object' ? String(opt.id) : String(opt),
        label: typeof opt === 'object' ? opt.label : String(opt),
      }))
    : [];

  // Find current selected option for controlled value
  // Handle both object values (from previous selection) and primitive values (from initial state)
  const findCurrentValue = (): ComboBoxOption | null => {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    // If value is already an object with id/label, find matching option
    if (typeof value === 'object' && value !== null && 'id' in value) {
      const objValue = value as { id: string | number; label?: string };
      return (
        comboBoxOptions.find(
          opt => opt.id === String(objValue.id) || opt.label === objValue.label
        ) || null
      );
    }

    // Value is a primitive (string/number), find by id or label
    const strValue = String(value);
    return comboBoxOptions.find(opt => opt.id === strValue || opt.label === strValue) || null;
  };

  const currentValue = findCurrentValue();
  const showEmptyChipStatic = currentValue === null && emptyDisplayNode;

  const clearOption: ComboBoxOption = {
    id: '__clear__',
    label: t`Set empty`,
    CustomElement: ClearFieldOption,
  };
  const optionsWithClear = showClearOption ? [...comboBoxOptions, clearOption] : comboBoxOptions;

  return (
    <ClearFieldFocusContainer key={col.key}>
      <ComboBox
        id={col.key}
        label={col.label}
        placeholder={showEmptyChipStatic ? '' : col.placeholder}
        options={optionsWithClear}
        inputVariant="outlined"
        labelSeparate
        forcePopupIcon
        popupIcon={<CaretDown size={20} />}
        hasAvatar={col.showAvatar}
        freeSolo={false}
        value={currentValue}
        onChange={(_, newValue, reason) => {
          if (reason === 'clear' && bulkEditContext?.isBulkEdit) {
            bulkEditContext.onKeepCurrent();
            return;
          }
          if (reason === 'clear') {
            handleChange(null);
            return;
          }
          if ((newValue as ComboBoxOption)?.id === '__clear__') {
            handleChange(bulkEditContext?.isBulkEdit ? BULK_EDIT_CLEAR : null);
          } else {
            handleChange(newValue);
          }
        }}
        disabled={disabled}
        InputProps={
          showEmptyChipStatic
            ? { startAdornment: emptyDisplayNode }
            : avatarAdornment
              ? { startAdornment: avatarAdornment }
              : undefined
        }
      />
      <FieldError error={errorMessage} show={showError} severity={errorSeverity} />
    </ClearFieldFocusContainer>
  );
};

// ============================================================================
// Read-Only Column Renderer
// ============================================================================

/**
 * Memoized chip cell for dropdown with colors
 */
const DropdownChipCell = memo(function DropdownChipCell({
  label,
  chipColor,
}: {
  label: string;
  chipColor: string;
}) {
  return (
    <Chip
      sx={{ padding: '4px 16px' }}
      isBordered={false}
      isBackgroundFilled
      isTooltip={false}
      label={label}
      color={chipColor}
      background={chipColor}
    />
  );
});

/**
 * Dropdown read-only renderer - displays value with optional avatar
 * Uses col.id as the data field
 * If optionColors is provided, renders a colored chip instead
 */
export const dropdownReadOnlyRenderer = <T extends { id: number | string }>(
  col: CustomColumn<T>,
  _options: RenderOptions<T>
): Partial<ColumnDef<T>> => {
  // Check if optionColors is provided for colored chip display
  if (col.optionColors) {
    return {
      cell: (context: CellContext<T, unknown>) => {
        const value = getCellValue(context, col.id);
        const id =
          typeof value === 'object' && value !== null
            ? (value as { id: string | number }).id
            : value;
        const label = getCellDisplayValue(context);
        const chipColor = col.optionColors?.[id as string | number] || 'grey';
        return <DropdownChipCell label={label} chipColor={chipColor} />;
      },
      tooltipRender: (cell: CellContext<T, unknown>) => getCellDisplayValue(cell),
    };
  }

  // Get showAvatar from column config (default true)
  const showAvatar = col.showAvatar ?? true;

  return {
    cell: (context: CellContext<T, unknown>) => {
      const cellValue = getCellDisplayValue(context);
      return <ReadOnlyDropdownCell value={cellValue || ''} showAvatar={showAvatar} />;
    },
    tooltipRender: (cell: CellContext<T, unknown>) => {
      // Tooltip also has CellContext, so use getCellDisplayValue
      return getCellDisplayValue(cell);
    },
  };
};

// ============================================================================
// Editable Column Renderer
// ============================================================================

/**
 * Dropdown editable renderer - unified pattern
 * Uses col.id as the data field, auto-infers update
 * Supports conditional editability via editable: (cell) => boolean
 * Supports both API-backed and static options
 */
export const dropdownEditableRenderer = <T extends { id: number | string }>(
  col: CustomColumn<T>,
  _options: RenderOptions<T>
): Partial<ColumnDef<T>> => {
  const resolvedApi = col.api || (getLookupApiFn ? getLookupApiFn(col.id) : null);
  const hasStaticOptions = col.options && col.options.length > 0;
  const hasNoDataSource = !resolvedApi && !hasStaticOptions;
  const showAvatar = col.showAvatar ?? Boolean(resolvedApi);
  const editableProp = col.editable;

  // If no data source available, render as read-only text
  // This gracefully handles custom fields where allowed values aren't loaded
  if (hasNoDataSource) {
    return {
      cell: (context: CellContext<T, unknown>) => {
        const cellValue = getCellDisplayValue(context);
        return <CellContainer>{cellValue || ''}</CellContainer>;
      },
      meta: {
        editable: false,
      },
    };
  }

  // If we have an API, use the API-backed EditableDropdownCell
  if (resolvedApi) {
    // Type assertion needed because BaseColumn api.url can be null, but we've already checked it exists
    const apiConfig = resolvedApi as {
      url: (params: {
        query?: URLSearchParams;
        vars?: string[];
      }) => Promise<ApiResponse<unknown, unknown>>;
      key: string;
      vars?: string[];
      revalidate?: boolean;
      search?: boolean;
      responseTransform: (
        response: Response<{ id: number; name: string; avatarColor?: string }[]>
      ) => Array<{ id: number | string; label: string; color?: string }>;
    };

    return {
      cell: (context: CellContext<T, unknown>) => {
        // Use getCellDisplayValue to respect accessorFn (important for custom fields)
        const cellValue = getCellDisplayValue(context);

        // Evaluate editability for this specific row
        const isRowEditable =
          typeof editableProp === 'function' ? editableProp(context) : editableProp === true;

        // If not editable for this row, show tooltip when disabled with a reason
        if (!isRowEditable) {
          const disabledTooltip = col.editDisabledPlaceholder
            ? typeof col.editDisabledPlaceholder === 'function'
              ? col.editDisabledPlaceholder()
              : col.editDisabledPlaceholder
            : undefined;
          const displayValue = cellValue || '';
          const showPlaceholder = !cellValue && Boolean(disabledTooltip);
          const cell = (
            <CellContainer
              style={{
                color: showPlaceholder ? '#999' : 'inherit',
                fontStyle: showPlaceholder ? 'italic' : 'normal',
              }}
            >
              {displayValue || (showPlaceholder ? disabledTooltip : '')}
            </CellContainer>
          );
          return disabledTooltip ? (
            <TableTooltip title={disabledTooltip}>{cell}</TableTooltip>
          ) : (
            cell
          );
        }

        return (
          <EditableDropdownCell<T>
            context={context}
            api={apiConfig}
            value={cellValue}
            showAvatar={showAvatar}
            required={col.required}
            onChange={(e, selectedValue) => {
              if (!col.onUpdate) return;
              let result: void | Promise<void>;
              if (
                selectedValue &&
                typeof selectedValue !== 'string' &&
                !Array.isArray(selectedValue) &&
                selectedValue.id
              ) {
                if (selectedValue.id === '__clear__') {
                  if ('required' in col) {
                    toast.error(t`${col.label || 'This field'} is required and cannot be cleared`);
                    return;
                  }
                  result = col.onUpdate(context.row.original, { [col.id]: null } as Partial<T>);
                } else {
                  const updateValue = {
                    id: selectedValue.id,
                    name: selectedValue.label || '',
                  };
                  result = col.onUpdate(context.row.original, {
                    [col.id]: updateValue,
                  } as Partial<T>);
                }
              } else {
                if ('required' in col) {
                  toast.error(t`${col.label || 'This field'} is required and cannot be cleared`);
                  return;
                }
                result = col.onUpdate(context.row.original, { [col.id]: null } as Partial<T>);
              }
              return result;
            }}
          />
        );
      },
      meta: {
        editable: true,
      },
    };
  }

  // Static options path - use Autocomplete with static options
  // Get color from optionColors map if provided
  const getChipColor = (id: string | number) => col.optionColors?.[id] || 'grey';

  return {
    cell: (context: CellContext<T, unknown>) => {
      // Use getCellDisplayValue to respect accessorFn (important for custom fields)
      const cellValue = getCellDisplayValue(context);

      // Extract id directly from cell value for color lookup (more reliable than string matching)
      const rawValue = getCellValue(context, col.id);
      const valueId =
        typeof rawValue === 'object' && rawValue !== null
          ? (rawValue as { id: string | number }).id
          : rawValue;

      // Evaluate editability for this specific row
      const isRowEditable =
        typeof editableProp === 'function' ? editableProp(context) : editableProp === true;

      // Find current value in options
      const currentOption = col.options?.find(
        opt => opt.label === cellValue || String(opt.id) === cellValue
      );

      // Check if value is valid (exists in allowed options)
      const isValid = isValueValid(cellValue, col.options);
      const tooltipText = cellValue ? (isValid ? cellValue : `${cellValue} (invalid value)`) : '';

      // Read-only display - use chip if optionColors is provided
      // Use valueId for color lookup (falls back to currentOption?.id for compatibility)
      // Show InvalidTag if value doesn't exist in options
      const chipColor = getChipColor((valueId ?? currentOption?.id ?? '') as string | number);
      const readOnlyDisplay = col.optionColors ? (
        <Chip
          sx={{ padding: '4px 16px' }}
          isBordered={false}
          isBackgroundFilled
          isTooltip={false}
          label={cellValue}
          color={isValid ? chipColor : 'red'}
          background={isValid ? chipColor : 'red'}
        />
      ) : cellValue && !isValid ? (
        <TableTooltip title={tooltipText}>
          <CellContainer>
            <TagsContainer>
              <InvalidTag>{cellValue}</InvalidTag>
            </TagsContainer>
          </CellContainer>
        </TableTooltip>
      ) : (
        <CellContainer>{cellValue || ''}</CellContainer>
      );

      // If not editable for this row, show tooltip when disabled with a reason
      if (!isRowEditable) {
        const disabledTooltip = col.editDisabledPlaceholder
          ? typeof col.editDisabledPlaceholder === 'function'
            ? col.editDisabledPlaceholder()
            : col.editDisabledPlaceholder
          : undefined;
        // Check for empty value more explicitly (empty string, null, undefined, whitespace)
        const isEmpty = !cellValue || (typeof cellValue === 'string' && cellValue.trim() === '');
        if (isEmpty && disabledTooltip) {
          const placeholderCell = (
            <CellContainer style={{ color: '#999', fontStyle: 'italic' }}>
              {disabledTooltip}
            </CellContainer>
          );
          return <TableTooltip title={disabledTooltip}>{placeholderCell}</TableTooltip>;
        }
        return readOnlyDisplay;
      }

      // Filter options for hierarchical custom fields (NestedAllowedValue)
      let filteredOptions = col.options || [];

      // CROSS-FIELD DEPENDENCY: Filter based on parent custom field's selected value
      // If this field has a parentCustomFieldId, filter options to only show children
      // of the parent field's selected value
      if ((col as { parentCustomFieldId?: number }).parentCustomFieldId) {
        const parentCustomFieldId = (col as { parentCustomFieldId?: number }).parentCustomFieldId;

        // Get the parent field's value from the row
        // Find the parent field in the row's customFields array
        const parentFieldValue = (
          context.row.original as { customFields?: CustomFieldValue[] }
        ).customFields?.find(cf => cf.customFieldId === parentCustomFieldId);

        const parentSelectedId = parentFieldValue?.itemCustomFieldAllowedValueId;

        if (parentSelectedId) {
          // Parent has a value selected - filter to show only children of that value
          filteredOptions = filteredOptions.filter(
            (opt: ComboBoxOption) => opt.customProps?.parentId === parentSelectedId
          );

          // If no children found, keep empty (user must select parent first)
          // Don't fallback to all options like we do for within-field hierarchy
        }
        // If parent not selected, show no options (placeholder will show "Select parent first")
        else {
          filteredOptions = [];
        }
      }
      // WITHIN-FIELD HIERARCHY: Filter based on current field's selected value
      // Check if this is a hierarchical field by looking for parentId in customProps
      // Note: parentId can be null, undefined, or a number. We check if the property exists.
      const hasHierarchy = filteredOptions.some(
        (opt: ComboBoxOption) => opt.customProps?.parentId !== undefined
      );

      if (hasHierarchy) {
        // This is a hierarchical field - filter options based on current selection
        const currentValueId = currentOption?.id;

        // Helper to check if option is a parent-level option (no parent)
        const isParentOption = (opt: ComboBoxOption): boolean => {
          const parentId = opt.customProps?.parentId;
          return parentId === null || parentId === undefined || parentId === 0;
        };

        if (!currentValueId) {
          // No value selected - show only parent options
          filteredOptions = filteredOptions.filter(isParentOption);
        } else {
          // Value selected - show children of the selected value
          filteredOptions = filteredOptions.filter(
            (opt: ComboBoxOption) => opt.customProps?.parentId === currentValueId
          );

          // If no children found, show all parent-level options
          // (this allows user to change their selection)
          if (filteredOptions.length === 0) {
            filteredOptions = (col.options || []).filter(isParentOption);
          }
        }
        // Defensive fallback: if filtering resulted in no options, show all options
        // This handles edge cases where data might be malformed
        if (filteredOptions.length === 0) {
          filteredOptions = col.options || [];
        }
      }

      // Add "Clear" option at the top when there's a value (but not for required fields)
      const clearOption = { id: '__clear__', label: t`Clear` };
      const optionsWithClear =
        cellValue && !('required' in col) ? [clearOption, ...filteredOptions] : filteredOptions;

      return (
        <DropdownStaticOptionsCell
          context={context}
          col={col}
          cellValue={cellValue}
          currentOption={currentOption ?? null}
          optionsWithClear={optionsWithClear}
          readOnlyDisplay={readOnlyDisplay}
        />
      );
    },
    meta: {
      editable: true,
    },
  };
};

/** Wrapper that uses editable context for static-options dropdown path */
const DropdownStaticOptionsCell = <T extends { id: number | string }>({
  context,
  col,
  cellValue,
  currentOption,
  optionsWithClear,
  readOnlyDisplay,
}: {
  context: CellContext<T, unknown>;
  col: CustomColumn<T>;
  cellValue: string;
  currentOption: ComboBoxOption | null;
  optionsWithClear: ComboBoxOption[];
  readOnlyDisplay: React.ReactNode;
}) => {
  const editableCtx = useEditableContextOptional();
  const setEditableState = editableCtx?.setEditableState ?? ((): void => undefined);
  const clearEdit = useCallback(
    () => setEditableState({ rowIndex: null, columnId: null }),
    [setEditableState]
  );

  return (
    <SharedEditableCell
      {...context}
      meta={{ editable: true }}
      tooltipTitle={cellValue}
      readModeEl={readOnlyDisplay}
    >
      <Autocomplete
        id={`${col.id}-edit`}
        options={optionsWithClear}
        variant="standard"
        forcePopupIcon
        popupIcon={<CaretDown size={20} />}
        value={currentOption ?? null}
        onChange={(_, newValue) => {
          if (!col.onUpdate) {
            clearEdit();
            return;
          }
          let result: void | Promise<void>;
          if (newValue?.id === '__clear__') {
            if ('required' in col) {
              toast.error(t`${col.label || 'This field'} is required and cannot be cleared`);
              clearEdit();
              return;
            }
            result = col.onUpdate(context.row.original, { [col.id]: null } as Partial<T>);
          } else if (newValue && typeof newValue === 'object' && 'id' in newValue) {
            const updateValue = {
              id: newValue.id,
              name: newValue.label || '',
            };
            result = col.onUpdate(context.row.original, {
              [col.id]: updateValue,
            } as Partial<T>);
          } else {
            if ('required' in col) {
              toast.error(t`${col.label || 'This field'} is required and cannot be cleared`);
              clearEdit();
              return;
            }
            result = col.onUpdate(context.row.original, { [col.id]: null } as Partial<T>);
          }
          clearEditOnSuccess(result, clearEdit);
        }}
        onBlur={clearEdit}
        onKeyDown={e => {
          if (e.key === 'Enter') clearEdit();
        }}
        isOptionEqualToValue={(option, val) => {
          if (!option || !val) return false;
          if (option.id === '__clear__') return false;
          return String(option.id) === String(val.id);
        }}
        getOptionLabel={option => {
          if (typeof option === 'string') return option;
          if (option?.id === '__clear__') return '';
          return option?.label ?? '';
        }}
        autoFocus
        openOnFocus
        sx={{ minWidth: 150 }}
      />
    </SharedEditableCell>
  );
};

// ============================================================================
// Field Type Configuration
// ============================================================================

/**
 * Dropdown field type configuration
 */
export const dropdownFieldType: FieldTypeConfig = {
  placeholder: (label: string) => `Select ${label.toLowerCase()}...`,
  ...M,
};

// ============================================================================
// Typed Renderer Exports (for registry)
// ============================================================================

/** Read-only renderer with proper typing */
export const dropdownReadOnly: ColumnRenderer = dropdownReadOnlyRenderer;

/** Editable renderer with proper typing */
export const dropdownEditable: ColumnRenderer = dropdownEditableRenderer;
